import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, RotateCcw, ShieldCheck } from 'lucide-react';
import { formatDate, getCurrentUser } from '../utils/helpers';

const empty = { name: '', email: '', password: '', role: 'staff', phone: '', status: 1 };

// Roles an admin may assign to users they create. The 'admin' role is NOT in
// this list — admins can only be created by self-registration on the login
// page; they own a separate organization tree.
const ASSIGNABLE_ROLES = [
  { value: 'staff', label: 'Staff' },
  { value: 'manager', label: 'Manager' },
  { value: 'accountant', label: 'Accountant' },
];

export default function Users() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const me = getCurrentUser();
  const [filterStatus, setFilterStatus] = useState('active');

  const load = () => api.get('/users').then(r => setList(r.data.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.email) return toast.error('Name & email required');
    if (!editId && !form.password) return toast.error('Password required for new user');
    try {
      if (editId) await api.put('/users/' + editId, form);
      else await api.post('/users', form);
      toast.success(editId ? 'Updated' : 'User created. Share the login credentials with them.');
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };
  // Soft delete — the account is deactivated, not removed, so their records
  // keep an author. A deactivated user can no longer log in.
  const del = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try { await api.delete('/users/' + id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };
  const restore = async (id) => {
    if (!window.confirm('Reactivate this user?')) return;
    try { await api.post('/users/' + id + '/restore'); toast.success('Reactivated'); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };
  const edit = (u) => { setForm({ ...empty, ...u, password: '' }); setEditId(u.id); setOpen(true); };

  // Hide the "edit self" admin-as-self row's role/status controls in the modal
  const isEditingSelf = editId === me.id;
  const isEditingAdmin = !!editId && form.role === 'admin';

  const filtered = list.filter(u => {
    if (filterStatus === 'active') return Number(u.status) === 1;
    if (filterStatus === 'inactive') return Number(u.status) !== 1;
    return true;
  });

  return (
    <div className="card">
      <div className="card-header">
        <h3>Users / Team ({filtered.length})</h3>
        <button className="btn btn-blue" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}>
          <Plus size={14} /> Add Staff User
        </button>
      </div>

      <div style={{ padding: '10px 16px', background: '#eff6ff', color: '#1e40af', borderRadius: 8, margin: '8px 14px', fontSize: 13, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <ShieldCheck size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          Users you create here are part of <strong>your organization only</strong>. They cannot see other admins' data.
          They sign in with the email and password you set, and any record they create needs your approval before it takes effect.
        </div>
      </div>

      <div className="search-bar" style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{maxWidth:180}}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">Active + Inactive</option>
        </select>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Phone</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.length ? filtered.map(u => (
              <tr key={u.id} style={Number(u.status)!==1?{background:'#f8fafc',color:'#94a3b8'}:{}}>
                <td>
                  <strong>{u.name}</strong>
                  {u.id === me.id && <small style={{ color: '#64748b', marginLeft: 6 }}>(you)</small>}
                </td>
                <td>{u.email}</td>
                <td>
                  <span className={'badge ' + (u.role === 'admin' ? 'badge-blue' : 'badge-gray')} style={{ textTransform: 'capitalize' }}>
                    {u.role}
                  </span>
                </td>
                <td>{u.phone || '-'}</td>
                <td><span className={'badge ' + (u.status == 1 ? 'badge-green' : 'badge-red')}>{u.status == 1 ? 'Active' : 'Inactive'}</span></td>
                <td>{formatDate(u.created_at)}</td>
                <td>
                  <button className="btn btn-sm btn-gray" onClick={() => edit(u)} title="Edit"><Edit2 size={12} /></button>
                  {u.id !== me.id && (
                    Number(u.status) !== 1 ? (
                      <>{' '}<button className="btn btn-sm" style={{background:'#10b981',color:'white'}} onClick={() => restore(u.id)} title="Reactivate"><RotateCcw size={12} /></button></>
                    ) : (
                      <>{' '}<button className="btn btn-sm btn-red" onClick={() => del(u.id)} title="Delete"><Trash2 size={12} /></button></>
                    )
                  )}
                </td>
              </tr>
            )) : <tr><td colSpan="7" className="empty">No users yet</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? 'Edit User' : 'Add Staff User'}
        footer={<>
          <button className="btn btn-gray" onClick={() => setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>{editId ? 'Update' : 'Create User'}</button>
        </>}>
        <div className="form-grid">
          <div className="form-group"><label>Name *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
          <div className="form-group"><label>Email *</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div className="form-group"><label>Password {editId && '(leave blank to keep)'}</label><input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
          <div className="form-group">
            <label>Role</label>
            {isEditingAdmin ? (
              <input value="Admin" disabled style={{ background: '#f1f5f9' }} />
            ) : (
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                {ASSIGNABLE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            )}
          </div>
          <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="form-group">
            <label>Status</label>
            {isEditingSelf ? (
              <input value="Active" disabled style={{ background: '#f1f5f9' }} />
            ) : (
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                <option value={1}>Active</option><option value={0}>Inactive</option>
              </select>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
